import {
  Avatar,
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { useGetMCPConnectorInfo } from 'src/content/applications/Users/settings/profile/hooks/useGetMCPConnectorInfo';
import {
  firstLetterName,
  handleInterComClick,
  isLocalSeoUser,
  localStorageMethod,
  openNewTab
} from 'src/common/config/FunctionHelper';
import {
  communityLink,
  diy,
  diyRestrictedMenus,
  expert,
  LocalSEO,
  planNames,
  pro,
  starter,
  stripeStatusTrial,
  supportText,
  tutorialLink
} from 'src/common/config/TextMessage';
import { useNavigate } from 'react-router-dom';
import '../index.css';
import { useState } from 'react';
import { userIndex } from './indexCSS';
import LogOutIcon from 'src/components/Icons/Logout';
import Tutorials from 'src/components/Icons/Tutorials';
import Community from 'src/components/Icons/Community';
import BillingIcon from 'src/components/Icons/BillingIcon';
import { paths } from 'src/common/config/TextMessage';
import LogoutModel from 'src/layouts/LogoutModel';
import UserIcon from 'src/components/Icons/User';
import MultipleUserIcon from 'src/components/Icons/MultipleUser';
import MCPModal from 'src/layouts/MCPModal';
import PlanTrialDropdown from './PlanTrialDropdown';
/**
 * A component displaying user details and active plan information in the header.
 * @returns {JSX.Element} The HeaderUserbox component.
 */
const HeaderUserbox = ({ openIntercom = true }) => {
  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  const subscription: any = useSelector(
    (state: RootState) => state.user.subscription
  );
  let stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  let isPremiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const stripeLoader =
    useSelector((state: RootState) => state.siteAudit.stripeLoader) ?? true;
  // Check if the site audit loader is active
  const loader =
    useSelector((state: RootState) => state.siteAudit.loader) ?? true;
  const isMember = userDetails?.role_type === 'member';
  const isDiyOrFreePlan =
    activePlanName === planDetails.diy ||
    activePlanName === planNames.starter ||
    activePlanName === planNames.starterPlus ||
    activePlanName === planDetails.free;
  const isNotPause = subscription && !subscription?.pause_collection?.behavior;
  const isFreePlan = activePlanName === planDetails.free;
  const isNoPlanAccount =
    !stripeLoader &&
    !loader &&
    userDetails?.email &&
    userDetails?.temp == true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);
  const isExpireAccount =
    !stripeLoader &&
    !loader &&
    !isFreePlan &&
    userDetails?.email &&
    userDetails?.temp !== true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);
  const isOldPlan = stripeSubscriptionDetails?.planVersion == 'v1';

  const isNoPlanOrExpire = isNoPlanAccount || isExpireAccount;
  const isLocalSeo = isLocalSeoUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [openMcpModal, setOpenMcpModal] = useState(false);

  const { data: mcpConnectorInfo, isLoading: isMcpConnectorLoading } =
    useGetMCPConnectorInfo();
  const isMcpConnected = mcpConnectorInfo?.status === 'connected';

  let menuItems = [
    {
      label: 'Personal Profile',
      icon: <UserIcon />,
      onClick: () => {
        handleClose();
        navigate(paths?.settings);
        if (!isLocalSeo) {
          // Remove items from localStorage
          localStorageMethod('remove', 'squareSpaceSite');
          localStorageMethod('remove', 'squareSpaceSiteId');
          localStorageMethod('remove', 'squareSpaceAccountId');
        }
      }
    },
    {
      label: 'Billing Information',
      icon: <BillingIcon />,
      onClick: () => {
        handleClose();
        navigate(paths?.billing);
        if (!isLocalSeo) {
          // Remove items from localStorage
          localStorageMethod('remove', 'squareSpaceSite');
          localStorageMethod('remove', 'squareSpaceSiteId');
          localStorageMethod('remove', 'squareSpaceAccountId');
        }
      }
    },
    {
      label: 'User Management',
      icon: <MultipleUserIcon />,
      onClick: () => {
        handleClose();
        navigate(paths?.userManagement);
      }
    },
    {
      label: 'Claude MCP',
      icon: (
        <img
          src={
            process.env.PUBLIC_URL +
            '/static/images/icons/ai-visibility/ai-icons/claude_002.svg'
          }
          alt="Claude MCP"
          width={20}
          height={20}
        />
      ),
      badge: isMcpConnectorLoading ? (
        <CircularProgress size={12} sx={{ ml: 1.5 }} />
      ) : (
        <Box
          sx={{
            ml: 1.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.2,
            borderRadius: '10px',
            backgroundColor: isMcpConnected
              ? 'rgba(46, 125, 50, 0.08)'
              : 'rgba(211, 47, 47, 0.08)',
            border: `1px solid ${
              isMcpConnected
                ? 'rgba(46, 125, 50, 0.3)'
                : 'rgba(211, 47, 47, 0.3)'
            }`
          }}
        >
          <FiberManualRecordIcon
            sx={{
              fontSize: 6,
              color: isMcpConnected ? '#2e7d32' : '#d32f2f'
            }}
          />
          <Typography
            component="span"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '10px',
              lineHeight: '14px',
              color: isMcpConnected ? '#2e7d32' : '#d32f2f',
              textTransform: 'capitalize'
            }}
          >
            {mcpConnectorInfo?.status ?? 'Disconnected'}
          </Typography>
        </Box>
      ),
      onClick: () => {
        handleClose();
        openMcpModalHandler();
      }
    },
    {
      label: 'Community',
      icon: <Community />,
      onClick: () => {
        handleClose();
        openNewTab(communityLink);
      }
    },
    {
      label: 'Tutorials',
      icon: <Tutorials />,
      onClick: () => {
        handleClose();
        openNewTab(tutorialLink);
      }
    },
    {
      isDivider: true
    },
    {
      label: 'Logout',
      icon: <LogOutIcon />,
      onClick: () => {
        handleClose();
        openLogoutModalHandler();
      }
    }
  ];
  if (
    (isDiyOrFreePlan && isNotPause) ||
    isNoPlanOrExpire ||
    isMember ||
    isLocalSeo
  ) {
    menuItems = menuItems?.filter(
      (item) => !diyRestrictedMenus.includes(item.label)
    );
  }
  const handleClick = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const openLogoutModalHandler = () => {
    setOpenLogoutModal(true);
  };

  const closeLogoutModalHandler = () => {
    setOpenLogoutModal(false);
  };

  const openMcpModalHandler = () => {
    setOpenMcpModal(true);
  };

  const closeMcpModalHandler = () => {
    setOpenMcpModal(false);
  };

  const isTrial =
    stripeSubscriptionDetails?.status === stripeStatusTrial ||
    stripeSubscriptionDetails?.status === 'trialing';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
      }}
    >
      {/* Plan / Trial Dropdown (rendered only when user is on trial) */}
      {isTrial && <PlanTrialDropdown />}

      {/* Support Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          flexShrink: 0
        }}
      >
        <Typography
          sx={{
            cursor: 'pointer',
            fontFamily: 'Inter',
            fontSize: 'var(--font-size-14)',
            fontWeight: 600,
            color: 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap'
          }}
          onClick={openIntercom ? handleInterComClick : () => {}}
        >
          <img
            src={
              process.env.PUBLIC_URL +
              `/static/images/icons/keyword/bx_support.svg`
            }
            alt="icon"
            height={24}
            width={24}
          />
          <span className="ml-1" style={{ whiteSpace: 'nowrap' }}>
            <Box
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              {supportText}
            </Box>
          </span>
        </Typography>
      </Box>
      {/* Vertical Line */}
      {userDetails && userDetails['name'] && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: { xs: 1, sm: 2 },
            flexShrink: 0
          }}
        >
          <Box
            sx={{
              width: '1px',
              height: '24px',
              backgroundColor: 'var(--color-border)',
              opacity: 0.7
            }}
          />
        </Box>
      )}
      {/* User Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'end',
          marginRight: '8px',
          flexShrink: 0
        }}
      >
        <Typography
          className="capitalize"
          sx={{
            fontFamily: 'Inter',
            fontSize: { xs: '10px', ns: '14px' },
            fontWeight: 600,
            color: 'var(--color-text-heading)',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          }}
        >
          {userDetails?.['name']}
        </Typography>
        <Typography
          className="capitalize"
          sx={{
            fontFamily: 'Inter',
            fontSize: { xs: '10px', ns: '14px' },
            color: 'var(--color-text-grey-purple)',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            marginTop: { xs: '2px', md: 'unset' }
          }}
        >
          {activePlanName === planNames.localseo
            ? LocalSEO
            : activePlanName === planNames.diy ||
              activePlanName === planNames.starter ||
              activePlanName === planNames.starterPlus
            ? isOldPlan
              ? diy
              : activePlanName === planNames.starterPlus
              ? starter
              : starter
            : activePlanName == planNames.designer ||
              activePlanName == planNames.proPlus
            ? pro
            : activePlanName == planNames.agency ||
              activePlanName == planNames.expertPlus
            ? expert
            : activePlanName}
        </Typography>
      </Box>
      {/* Avatar Section */}
      <Box>
        {userDetails && userDetails?.['name'] && (
          <Avatar
            sx={{
              bgcolor: 'var(--color-primary)',
              cursor: 'pointer',
              alignSelf: 'center'
            }}
            onClick={handleClick}
          >
            <p className="text-themeWhite font-bold text-base uppercase">
              {firstLetterName(userDetails?.['name'])}
            </p>
          </Avatar>
        )}
        {/* Menu List Option */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableScrollLock={true}
          PaperProps={{ style: { borderRadius: '6px' } }}
        >
          {menuItems?.map((item, index) =>
            item.isDivider ? (
              <Divider key={index} />
            ) : (
              <MenuItem
                key={index}
                sx={{
                  ...userIndex.menuItem,
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-light-purple)' // Desired hover color
                  }
                }}
                onClick={item.onClick}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: '30px !important',
                      color: 'var(--color-primary) !important'
                    }}
                  >
                    {item?.icon}
                  </ListItemIcon>
                  {item?.label}
                </Box>
                {item?.badge}
              </MenuItem>
            )
          )}
        </Menu>
      </Box>
      <LogoutModel open={openLogoutModal} onClose={closeLogoutModalHandler} />
      <MCPModal open={openMcpModal} onClose={closeMcpModalHandler} />
    </Box>
  );
};

export default HeaderUserbox;
